import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  Badge,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  SimpleGrid,
  Divider,
  Avatar,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaChargingStation,
  FaBolt,
  FaUser,
  FaCalendarAlt,
  FaChevronRight,
} from "react-icons/fa";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";

const MotionBox = motion(Box);

const GlassMorphismCard = ({ children, ...props }) => {
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.2)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(20px)"
      borderRadius="20px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="xl"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "20px",
        padding: "1px",
        background: `linear-gradient(135deg, ${borderColor}, transparent)`,
        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        maskComposite: "subtract",
        pointerEvents: "none",
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    yet_to_be_approved: { colorScheme: "orange", label: "Pending" },
    approved: { colorScheme: "green", label: "Approved" },
    rejected: { colorScheme: "red", label: "Rejected" },
    paid: { colorScheme: "blue", label: "Paid" },
  };

  const config = statusConfig[status] || { colorScheme: "gray", label: status };

  return (
    <Badge
      colorScheme={config.colorScheme}
      variant="subtle"
      borderRadius="full"
      px={3}
      py={1}
      fontSize="sm"
      fontWeight="semibold"
      textTransform="capitalize"
    >
      {config.label}
    </Badge>
  );
};

const TypeBadge = ({ type }) => {
  const typeConfig = {
    property_owner: { colorScheme: "purple", label: "Property Owner" },
    cpo: { colorScheme: "blue", label: "CPO" },
    bco: { colorScheme: "green", label: "BCO" },
    cpo_operated_by_urzza: { colorScheme: "orange", label: "CPO (Urzza)" },
  };

  const config = typeConfig[type] || { colorScheme: "gray", label: type };

  return (
    <Badge
      colorScheme={config.colorScheme}
      variant="outline"
      borderRadius="full"
      px={3}
      py={1}
      fontSize="sm"
      fontWeight="medium"
    >
      {config.label}
    </Badge>
  );
};

const PayoutDetails = () => {
  const navigate = useNavigate();
  const { payrunId, payoutId } = useParams();
  const setToast = useSetRecoilState(toastState);
  const [payoutData, setPayoutData] = useState(null);

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const bgColor = useColorModeValue("gray.50", "gray.900");

  const [{ loading }, fetchPayout] = useAxios(
    {
      url: `/api/payouts/${payoutId}`,
      method: "GET",
    },
    { manual: true },
  );

  const fetchPayoutData = useCallback(async () => {
    try {
      const response = await fetchPayout();
      const payout = response.data.data;

      if (payout) {
        setPayoutData(payout);
      }
    } catch (error) {
      logger.error("Error fetching payout data:", error);
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error Occurred",
          status: "error",
          description:
            error?.response?.data?.error || "Failed to fetch payout data",
        });
      }
    }
  }, [fetchPayout, setToast]);

  useEffect(() => {
    if (payoutId) {
      fetchPayoutData();
    }
  }, [payoutId, fetchPayoutData]);

  if (loading) {
    return (
      <Box px={{ base: 4, md: 6 }} bg={bgColor} minH="100vh">
        <Center h="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="orange.500" thickness="4px" />
            <Text color={textColor}>Loading payout details...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (!payoutData) {
    return (
      <Box px={{ base: 4, md: 6 }} bg={bgColor} minH="100vh">
        <VStack spacing={4} py={12}>
          <Text fontSize="lg" color={subTextColor}>
            No payout data available
          </Text>
          <Button
            leftIcon={<Icon as={FaArrowLeft} />}
            onClick={() => navigate(`/admin/payouts/payrun/${payrunId}`)}
            colorScheme="orange"
            variant="outline"
          >
            Back to Payouts
          </Button>
        </VStack>
      </Box>
    );
  }

  const {
    payee_name,
    user_id,
    type,
    net_amount,
    gross_amount,
    tax_amount,
    network_charge_amount,
    status,
    beneficiary_name,
    beneficiary_account_number,
    beneficiary_ifsc,
    month,
    year,
    payrun_id,
    breakdown = [],
  } = payoutData;

  const isPropertyOwner = type === "property_owner";

  return (
    <Box px={{ base: 4, md: 6 }} bg={bgColor} minH="100vh">
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        w="100%"
      >
        <VStack spacing={6} align="stretch">
          {/* Breadcrumb */}
          <Breadcrumb
            spacing={2}
            separator={
              <Icon as={FaChevronRight} color={subTextColor} boxSize={3} />
            }
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate("/admin/payouts")}
                color="#f57d36"
                _hover={{ textDecoration: "underline" }}
              >
                Payouts
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate(`/admin/payouts/payrun/${payrun_id}`)}
                color="#f57d36"
                _hover={{ textDecoration: "underline" }}
              >
                {month} {year}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink color={textColor}>{payee_name}</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          {/* Header */}
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Button
                leftIcon={<Icon as={FaArrowLeft} />}
                onClick={() => navigate(`/admin/payouts/payrun/${payrun_id}`)}
                variant="ghost"
                colorScheme="orange"
              >
                Back
              </Button>
              <Divider orientation="vertical" h="30px" />
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  Payout Details
                </Text>
                <Text fontSize="sm" color={subTextColor}>
                  {month} {year} - Payrun #{payrun_id}
                </Text>
              </VStack>
            </HStack>
            <StatusBadge status={status} />
          </Flex>

          {/* Payee Information Card */}
          <GlassMorphismCard p={6}>
            <VStack spacing={6} align="stretch">
              <Text fontSize="md" fontWeight="600" color={textColor}>
                Payee Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <VStack align="start" spacing={2}>
                  <HStack spacing={2}>
                    <Icon as={FaUser} color="#f57d36" boxSize={4} />
                    <Text
                      fontSize="xs"
                      color={subTextColor}
                      fontWeight="medium"
                    >
                      PAYEE NAME
                    </Text>
                  </HStack>
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={payee_name}
                      bg="#f57d36"
                      color="white"
                    />
                    <VStack align="start" spacing={0}>
                      <Text
                        fontSize="md"
                        fontWeight="semibold"
                        color={textColor}
                      >
                        {payee_name || "N/A"}
                      </Text>
                      <Text fontSize="xs" color={subTextColor}>
                        ID: {user_id}
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>

                <VStack align="start" spacing={2}>
                  <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                    TYPE
                  </Text>
                  <TypeBadge type={type} />
                </VStack>

                <VStack align="start" spacing={2}>
                  <HStack spacing={2}>
                    <Icon as={FaCalendarAlt} color="#f57d36" boxSize={4} />
                    <Text
                      fontSize="xs"
                      color={subTextColor}
                      fontWeight="medium"
                    >
                      PERIOD
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600" color={textColor}>
                    {month} {year}
                  </Text>
                  <Text fontSize="xs" color={subTextColor}>
                    Payrun ID: {payrun_id}
                  </Text>
                </VStack>

                <VStack align="start" spacing={2}>
                  <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                    STATUS
                  </Text>
                  <StatusBadge status={status} />
                </VStack>
              </SimpleGrid>
            </VStack>
          </GlassMorphismCard>

          {/* Payment Summary */}
          <GlassMorphismCard p={6}>
            <VStack spacing={6} align="stretch">
              <Text fontSize="md" fontWeight="600" color={textColor}>
                Payment Summary
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Stat>
                  <StatLabel color={subTextColor} fontSize="sm">
                    Gross Amount
                  </StatLabel>
                  <StatNumber color="blue.500" fontSize="2xl">
                    ₹
                    {gross_amount?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }) || "0.00"}
                  </StatNumber>
                  <StatHelpText color={subTextColor}>
                    Total before deductions
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel color={subTextColor} fontSize="sm">
                    Tax Amount {isPropertyOwner ? "(Gain)" : "(Deduction)"}
                  </StatLabel>
                  <StatNumber
                    color={isPropertyOwner ? "green.500" : "red.500"}
                    fontSize="2xl"
                  >
                    {isPropertyOwner ? "+" : "-"}₹
                    {tax_amount?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }) || "0.00"}
                  </StatNumber>
                  <StatHelpText color={subTextColor}>
                    {isPropertyOwner ? "Tax benefit" : "Tax deducted"}
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel color={subTextColor} fontSize="sm">
                    Network Charge
                  </StatLabel>
                  <StatNumber color="red.500" fontSize="2xl">
                    -₹
                    {network_charge_amount?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }) || "0.00"}
                  </StatNumber>
                  <StatHelpText color={subTextColor}>
                    Network charge deduction
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel color={subTextColor} fontSize="sm">
                    Net Amount
                  </StatLabel>
                  <StatNumber color="green.500" fontSize="2xl">
                    ₹
                    {net_amount?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }) || "0.00"}
                  </StatNumber>
                  <StatHelpText color={subTextColor}>
                    Final payout amount
                  </StatHelpText>
                </Stat>
              </SimpleGrid>
            </VStack>
          </GlassMorphismCard>

          {/* Bank Account Details */}
          <GlassMorphismCard p={6}>
            <VStack spacing={6} align="stretch">
              <Text fontSize="md" fontWeight="600" color={textColor}>
                Bank Account Details
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <VStack align="start" spacing={2}>
                  <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                    BENEFICIARY NAME
                  </Text>
                  <Text fontSize="sm" fontWeight="600" color={textColor}>
                    {beneficiary_name || "N/A"}
                  </Text>
                </VStack>

                <VStack align="start" spacing={2}>
                  <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                    ACCOUNT NUMBER
                  </Text>
                  <Text fontSize="sm" fontWeight="600" color={textColor}>
                    {beneficiary_account_number || "N/A"}
                  </Text>
                </VStack>

                <VStack align="start" spacing={2}>
                  <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                    IFSC CODE
                  </Text>
                  <Text fontSize="sm" fontWeight="600" color={textColor}>
                    {beneficiary_ifsc || "N/A"}
                  </Text>
                </VStack>
              </SimpleGrid>
            </VStack>
          </GlassMorphismCard>

          {/* Station-wise Breakdown */}
          {breakdown && breakdown.length > 0 ? (
            <GlassMorphismCard p={6}>
              <VStack spacing={6} align="stretch">
                <Text fontSize="md" fontWeight="600" color={textColor}>
                  Station-wise Breakdown
                </Text>
                <TableContainer
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Table variant="simple">
                    <Thead bg={headerBg}>
                      <Tr>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                        >
                          Station
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Units (kWh)
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Demand
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Hosting
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Electricity
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Urzza Charges
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          OCPI Charges
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Network Charge
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Fixed Income
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Gross Amount
                        </Th>
                        <Th
                          border="none"
                          color={subTextColor}
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          textTransform="uppercase"
                          py={4}
                          isNumeric
                        >
                          Net Amount
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {breakdown.map((station, index) => (
                        <Tr
                          key={station.station_id || index}
                          _hover={{ bg: hoverBg }}
                        >
                          <Td border="none" py={4}>
                            <HStack spacing={3}>
                              <Box
                                w="32px"
                                h="32px"
                                borderRadius="lg"
                                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color="white"
                              >
                                <Icon as={FaChargingStation} boxSize={4} />
                              </Box>
                              <VStack align="start" spacing={0}>
                                <Text
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color={textColor}
                                >
                                  {station.station_name || "Unknown Station"}
                                </Text>
                                <Text fontSize="xs" color={subTextColor}>
                                  ID: {station.station_id || "N/A"}
                                </Text>
                              </VStack>
                            </HStack>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <HStack spacing={1} justify="flex-end">
                              <Icon
                                as={FaBolt}
                                color="yellow.500"
                                boxSize={3}
                              />
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={textColor}
                              >
                                {station.total_units_consumed || 0}
                              </Text>
                            </HStack>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color={isPropertyOwner ? "green.500" : "red.500"}
                            >
                              {isPropertyOwner ? "+" : "-"}₹
                              {station.demand_amount?.toFixed(2) || "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color={isPropertyOwner ? "green.500" : "red.500"}
                            >
                              {isPropertyOwner ? "+" : "-"}₹
                              {station.hosting_amount?.toFixed(2) || "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color={isPropertyOwner ? "green.500" : "red.500"}
                            >
                              {isPropertyOwner ? "+" : "-"}₹
                              {station.electricity_amount?.toFixed(2) || "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="red.500"
                            >
                              -₹
                              {station.total_urzza_charges?.toFixed(2) ||
                                "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="red.500"
                            >
                              -₹
                              {station.ocpi_charge?.toFixed(2) || "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="red.500"
                            >
                              -₹
                              {station.network_charge_amount?.toFixed(2) ||
                                "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="green.500"
                            >
                              +₹
                              {station.external_fixed_income?.toFixed(2) ||
                                "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="blue.500"
                            >
                              ₹{station.gross_amount?.toFixed(2) || "0.00"}
                            </Text>
                          </Td>
                          <Td border="none" py={4} isNumeric>
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="green.500"
                            >
                              ₹{station.net_amount?.toFixed(2) || "0.00"}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </VStack>
            </GlassMorphismCard>
          ) : (
            <GlassMorphismCard p={6}>
              <VStack spacing={4} py={8}>
                <Icon
                  as={FaChargingStation}
                  boxSize={12}
                  color={subTextColor}
                  opacity={0.5}
                />
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  No Station Breakdown Available
                </Text>
                <Text fontSize="sm" color={subTextColor}>
                  This payout does not have station-wise breakdown data
                </Text>
              </VStack>
            </GlassMorphismCard>
          )}
        </VStack>
      </MotionBox>
    </Box>
  );
};

export default PayoutDetails;
